import React from 'react';

type ModalState = {
  isOpen: boolean;
  width: number | 'full';
  onClose: () => void;
  children: React.ReactNode;
  props: Record<string, string>;
  isPersistent?: boolean;
};

export enum ModalActions {
  Close = 'close',
  Toggle = 'toggle',
  SetChildren = 'setChildren',
  SetWidth = 'setWidth',
  Open = 'open',
  Reset = 'reset',
}

type Payload = {
  [ModalActions.Close]: undefined;
  [ModalActions.Toggle]: undefined;
  [ModalActions.SetChildren]: {
    children: React.ReactNode;
  };
  [ModalActions.SetWidth]: { width: number };
  [ModalActions.Open]: {
    children: React.ReactNode;
    width: number | 'full';
    props?: Record<string, string>;
    isPersistent?: boolean;
  };
  [ModalActions.Reset]: undefined;
};

const initialModalState = {
  isOpen: false,
  width: 500,
  onClose: () => null,
  children: null,
  props: {},
  isPersistent: true,
};

function reducer(state: ModalState, action: Action<Payload>): ModalState {
  switch (action.type) {
    case ModalActions.Close:
      return {
        ...state,
        isOpen: false,
      };
    case ModalActions.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ModalActions.SetChildren:
      return {
        ...state,
        children: action.children,
      };
    case ModalActions.SetWidth:
      return {
        ...state,
        width: action.width,
      };
    case ModalActions.Open:
      return {
        ...state,
        isOpen: true,
        children: action.children,
        width: action.width,
        props: {
          ...action.props,
        },
        isPersistent: action.isPersistent,
      };
    case ModalActions.Reset:
      return initialModalState;
    default:
      return state;
  }
}

export const ModalContext = React.createContext<{
  state: ModalState;
  dispatch: React.Dispatch<Action<Payload>>;
}>({ state: initialModalState, dispatch: () => null });
ModalContext.displayName = 'ModalContext';

export const ModalProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children = null }) => {
  const [state, dispatch] = React.useReducer(reducer, initialModalState);

  const value = React.useMemo(() => ({ state, dispatch }), [state]);

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};
